// src/features/simulators/shared/hooks/useAnalyticsClient.ts

import { useState, useCallback, useRef, useEffect } from 'react';
import { AnalyticsClient } from '../utils/analyticsClient'

const CLIENT_NOT_INITIALIZED_ERROR = 'simulator.device.client_not_initialized';

interface UseAnalyticsClientProps {
  apiUrl: string;
}

interface UseAnalyticsClientReturn {
  client: AnalyticsClient | null;
  isRegistered: boolean;
  isLoading: boolean;
  error: string | null;
  registerDevice: (installationCode: string) => Promise<{ hotelId: string; groupId: string | null }>;
  clearError: () => void;
}

/**
 * Hook to manage AnalyticsClient instance
 * Handles device registration and provides client for sending events
 */
export const useAnalyticsClient = ({
  apiUrl,
}: UseAnalyticsClientProps): UseAnalyticsClientReturn => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const clientRef = useRef<AnalyticsClient | null>(null);

  /**
   * Initialize client on mount
   */
  useEffect(() => {
    if (!clientRef.current) {
      clientRef.current = new AnalyticsClient(apiUrl);
    }
  }, [apiUrl]);

  /**
   * Register device with installation code
   * Returns hotelId and groupId from registration response
   */
  const registerDevice = useCallback(async (
    installationCode: string
  ): Promise<{ hotelId: string; groupId: string | null }> => {
    if (!clientRef.current) {
      throw new Error(CLIENT_NOT_INITIALIZED_ERROR);
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await clientRef.current.registerDevice({
        installationCode,
      });

      if (response.success && response.data.apiKey) {
        setIsRegistered(true);

        return {
          hotelId: response.data.hotelId,
          groupId: response.data.groupId,
        };
      } else {
        throw new Error('Registration failed: No API key received');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    client: clientRef.current,
    isRegistered,
    isLoading,
    error,
    registerDevice,
    clearError,
  };
};